import { useEffect, useRef } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { ChatPanelContainer, VisuallyHidden } from "./styled/ChatStyled";

import { AiChatLevelAtom, AiImageQueueAtom, AiImagePickerAtom } from "../../state/aiChatAtoms";
import { ImageGenerationProgress } from "../ImageGenerationProgress";
import { ImagePickerModal } from "../ImagePickerModal";
import ChatCodeMirror from "./ChatCodeMirror";
import InlineElementEditor from "./InlineElementEditor";

function AiChatPanel() {
	const level = useRecoilValue(AiChatLevelAtom);
	const setLevel = useSetRecoilState(AiChatLevelAtom);
	const imageQueue = useRecoilValue(AiImageQueueAtom);
	const imagePicker = useRecoilValue(AiImagePickerAtom);
	const panelRef = useRef<HTMLElement>(null);

	const hasFailures =
		imageQueue.length > 0 &&
		imageQueue.some((s) => s === "failed") &&
		!imageQueue.some((s) => s === "generating" || s === "pending");
	const processingImages =
		imageQueue.length > 0 &&
		(imageQueue.some((s) => s !== "done" && s !== "failed") || hasFailures);

	// Focus the chat input when the panel opens
	useEffect(() => {
		const input = document.getElementById("chat-input");
		input?.focus();
	}, []);

	// Escape returns to code editor (Level 3)
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				setLevel(3);
			}
		};
		const el = panelRef.current;
		el?.addEventListener("keydown", handleKeyDown);
		return () => el?.removeEventListener("keydown", handleKeyDown);
	}, [setLevel]);

	return (
		<ChatPanelContainer
			ref={panelRef}
			role="region"
			aria-label="AI chat assistant"
		>
			{imagePicker && <ImagePickerModal />}

			{!imagePicker && processingImages && <ImageGenerationProgress queue={imageQueue} />}

			{!imagePicker && !processingImages && level === 1 && <ChatCodeMirror />}

			{!imagePicker && !processingImages && level === 2 && <InlineElementEditor />}

			<VisuallyHidden aria-live="assertive">
				{processingImages && "Generating images"}
				{!processingImages && level === 1 && "Chat mode active"}
				{!processingImages && level === 2 && "Element editing mode active"}
				{level === 3 && "Code editor active"}
			</VisuallyHidden>
		</ChatPanelContainer>
	);
}

export default AiChatPanel;
